import { useState, useRef, useEffect, useCallback } from 'react';
import { SyncFolderService } from '../services/sync-folder';
import {
    canPickExplorerParentFolder,
    explorerFolderWarningSummary,
    pickExplorerParentFolder,
    scanExplorerParentFolder,
} from '../services/file-explorer-intake';
import { getFolderAccessUnsupportedMessage } from '../services/folder-access-support';
import { failedJobToastText } from '../components/file-explorer/import-workflow';

export interface GlobalFileExplorerState {
    open: boolean;
    parent: any;
    projects: any[];
    selectedProjectId: string | null;
    focusFailedJobId: string | null;
    scanning: boolean;
    importing: boolean;
    error: string;
    needsSyncFolder: boolean;
}

export interface UseGlobalFileExplorerOptions {
    setToast: (opts: any) => void;
    setParentFolder: (folder: any) => void;
    openSettings: (tab?: string) => void;
}

export function useGlobalFileExplorer({ setToast, setParentFolder, openSettings }: UseGlobalFileExplorerOptions) {
    const [globalFileExplorer, setGlobalFileExplorer] = useState<GlobalFileExplorerState>({
        open: false,
        parent: null,
        projects: [],
        selectedProjectId: null,
        focusFailedJobId: null,
        scanning: false,
        importing: false,
        error: '',
        needsSyncFolder: false,
    });

    const closeGlobalFileExplorer = useCallback(() => {
        setGlobalFileExplorer(prev => ({
            ...prev,
            open: false,
            importing: false,
            error: '',
            needsSyncFolder: false,
            focusFailedJobId: null,
        }));
    }, []);

    const globalFileExplorerRef = useRef(globalFileExplorer);
    useEffect(() => { globalFileExplorerRef.current = globalFileExplorer; }, [globalFileExplorer]);

    // Batch run visibility outside Import Projects
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const overlayIsOpen = () => !!globalFileExplorerRef.current?.open;
        const onStarted = (e: any) => {
            if (overlayIsOpen()) return;
            const d = e?.detail || {};
            if ((d.phase || 'prepare-quote') !== 'prepare-quote') return;
            const n = Number(d.totalJobs || d.pending || 0);
            setToast({
                message: n > 0
                    ? `Drafting ${n} quote${n === 1 ? '' : 's'}, one project at a time. Keep this tab open.`
                    : 'Drafting quotes, one project at a time. Keep this tab open.',
                type: 'info',
                duration: 6000,
            });
        };
        const onJobFailed = (e: any) => {
            if (overlayIsOpen()) return;
            // The job's own words, never the engine's line (2026-10-04 UAT
            // F2): the Queue drawer's words, pointing at Import Projects
            // because its list is not on screen.
            setToast({
                message: failedJobToastText(e?.detail || {}, { overlayClosed: true }),
                type: 'error',
                duration: 10000,
            });
        };
        window.addEventListener('qm-batch-queue-started', onStarted);
        window.addEventListener('qm-batch-job-failed', onJobFailed);
        return () => {
            window.removeEventListener('qm-batch-queue-started', onStarted);
            window.removeEventListener('qm-batch-job-failed', onJobFailed);
        };
    }, [setToast]);

    const scanIntoGlobalFileExplorer = useCallback(async (parent: any, { focusFailedJobId = null }: { focusFailedJobId?: string | null } = {}) => {
        const projects = await scanExplorerParentFolder(parent.handle);
        const warningSummary = explorerFolderWarningSummary(projects);
        setGlobalFileExplorer({
            open: true,
            parent,
            projects,
            selectedProjectId: projects[0]?.id || null,
            focusFailedJobId,
            scanning: false,
            importing: false,
            error: warningSummary,
            needsSyncFolder: false,
        });

        if (projects.length === 0) {
            setToast({ message: 'No project folders or supported project files were found in that folder.', type: 'warning' });
        }
    }, [setToast]);

    const openGlobalFileExplorer = useCallback(async (options: any = {}) => {
        const forcePick = options?.forcePick === true;
        const focusFailedJobId = options?.failedJobId ?? null;
        if (!canPickExplorerParentFolder()) {
            setGlobalFileExplorer({
                open: true,
                parent: null,
                projects: [],
                selectedProjectId: null,
                focusFailedJobId,
                scanning: false,
                importing: false,
                error: getFolderAccessUnsupportedMessage('File Explorer'),
                needsSyncFolder: false,
            });
            return;
        }

        try {
            let parent: any = null;
            let configuredHandleSeen = false;
            let configuredPermissionMessage = '';
            if (!forcePick) {
                const configuredHandle = await SyncFolderService.getHandle();
                if (configuredHandle) {
                    configuredHandleSeen = true;
                    const hasPermission = await SyncFolderService.ensurePermission({ passUserGesture: true });
                    if (hasPermission) {
                        parent = {
                            name: configuredHandle.name || 'Configured sync folder',
                            handle: configuredHandle,
                            source: 'configured-sync-folder',
                        };
                    } else {
                        configuredPermissionMessage = `Chrome needs permission to reopen "${configuredHandle.name || 'the configured sync folder'}". Allow folder access when prompted, or use Change Root to choose a different folder.`;
                    }
                }
            }

            if (!forcePick && !configuredHandleSeen) {
                setGlobalFileExplorer({
                    open: true,
                    parent: null,
                    projects: [],
                    selectedProjectId: null,
                    focusFailedJobId,
                    scanning: false,
                    importing: false,
                    error: '',
                    needsSyncFolder: true,
                });
                return;
            }

            setGlobalFileExplorer(prev => ({
                ...prev,
                open: true,
                scanning: true,
                importing: false,
                error: '',
                needsSyncFolder: false,
            }));

            if (!parent && configuredHandleSeen) {
                setGlobalFileExplorer({
                    open: true,
                    parent: null,
                    projects: [],
                    selectedProjectId: null,
                    focusFailedJobId,
                    scanning: false,
                    importing: false,
                    error: configuredPermissionMessage || 'Chrome could not reopen the configured sync folder.',
                    needsSyncFolder: false,
                });
                setToast({ message: configuredPermissionMessage || 'Chrome could not reopen the configured sync folder.', type: 'warning', duration: 7000 });
                return;
            }

            if (!parent) {
                parent = await pickExplorerParentFolder();
            }

            if (!parent?.handle) {
                setGlobalFileExplorer(prev => ({
                    ...prev,
                    scanning: false,
                    needsSyncFolder: prev.needsSyncFolder || (forcePick && !configuredHandleSeen),
                }));
                return;
            }

            if (forcePick || parent.source !== 'configured-sync-folder') {
                await SyncFolderService.setHandle(parent.handle);
            }

            setParentFolder({ name: parent.name || parent.handle?.name || 'Sync root', handle: parent.handle });
            await scanIntoGlobalFileExplorer(parent, { focusFailedJobId });
        } catch (err: any) {
            if (err?.name === 'AbortError') {
                setGlobalFileExplorer(prev => ({
                    ...prev,
                    open: !!prev.parent,
                    scanning: false,
                    importing: false,
                }));
                return;
            }
            const message = err?.message || 'Could not read the selected folder.';
            console.error('[App] File Explorer folder scan failed:', err);
            setGlobalFileExplorer(prev => ({
                ...prev,
                open: true,
                scanning: false,
                importing: false,
                error: message,
            }));
            setToast({ message, type: 'error' });
        }
    }, [scanIntoGlobalFileExplorer, setParentFolder, setToast]);

    const toggleGlobalFileExplorer = useCallback(() => {
        if (globalFileExplorerRef.current?.open) {
            closeGlobalFileExplorer();
            return;
        }
        openGlobalFileExplorer();
    }, [closeGlobalFileExplorer, openGlobalFileExplorer]);

    const fileExplorerSelectProject = useCallback((projectId: string | null) => {
        setGlobalFileExplorer(prev => ({ ...prev, selectedProjectId: projectId }));
    }, []);

    const fileExplorerChangeFolder = useCallback(() => {
        openGlobalFileExplorer({ forcePick: true });
    }, [openGlobalFileExplorer]);

    const fileExplorerShowToast = useCallback((message: string, type: string = 'info') => {
        setToast({ message, type, duration: type === 'error' ? 7000 : 4000 });
    }, [setToast]);

    const fileExplorerOpenSyncSettings = useCallback(() => {
        closeGlobalFileExplorer();
        openSettings('data');
    }, [closeGlobalFileExplorer, openSettings]);

    const rescanGlobalFileExplorer = useCallback(async () => {
        const parent = globalFileExplorer.parent;
        if (!parent?.handle) {
            await openGlobalFileExplorer();
            return;
        }

        setGlobalFileExplorer(prev => ({
            ...prev,
            scanning: true,
            importing: false,
            error: '',
        }));

        try {
            const projects = await scanExplorerParentFolder(parent.handle);
            const warningSummary = explorerFolderWarningSummary(projects);
            setGlobalFileExplorer(prev => ({
                ...prev,
                projects,
                selectedProjectId: projects.some(project => project.id === prev.selectedProjectId)
                    ? prev.selectedProjectId
                    : (projects[0]?.id || null),
                scanning: false,
                importing: false,
                error: warningSummary,
            }));
        } catch (err: any) {
            const message = err?.message || 'Could not rescan the folder.';
            console.error('[App] File Explorer folder rescan failed:', err);
            setGlobalFileExplorer(prev => ({
                ...prev,
                scanning: false,
                importing: false,
                error: message,
            }));
            setToast({ message, type: 'error' });
        }
    }, [globalFileExplorer.parent, openGlobalFileExplorer, setToast]);

    return {
        globalFileExplorer,
        setGlobalFileExplorer,
        globalFileExplorerRef,
        closeGlobalFileExplorer,
        toggleGlobalFileExplorer,
        scanIntoGlobalFileExplorer,
        openGlobalFileExplorer,
        fileExplorerSelectProject,
        fileExplorerChangeFolder,
        fileExplorerShowToast,
        fileExplorerOpenSyncSettings,
        rescanGlobalFileExplorer,
    };
}
